export type OperationalStatusLike = {
  status?: string | null;
  severity?: string | null;
};

export type DeviceStatusRollupLike = {
  device_count?: number | null;
  monitor_count?: number | null;
  device_ids?: Array<number | string> | null;
  monitor_ids?: Array<number | string> | null;
  monitor_details?: Array<{
    monitor_id: number | string;
    monitor_key?: string | null;
    monitor_label?: string | null;
    status?: string | null;
    status_label?: string | null;
    severity?: string | null;
    severity_label?: string | null;
    status_reason?: string | null;
    status_reason_label?: string | null;
    open_message_count?: number | string | null;
    critical_open_message_count?: number | string | null;
    primary_load_name?: string | null;
    primary_virtual_device_name?: string | null;
    active_loads?: Array<{
      id: number | string;
      load_name?: string | null;
      virtual_device_name?: string | null;
      zone_name?: string | null;
      event_name?: string | null;
    }> | null;
  }> | null;
  status_counts?: Record<string, number | string | null | undefined> | null;
  open_message_count?: number | null;
  critical_open_message_count?: number | null;
  monitor_status_counts?: Record<string, number | string | null | undefined> | null;
  monitor_severity_counts?: Record<string, number | string | null | undefined> | null;
  worst_status?: string | null;
  worst_status_label?: string | null;
  worst_severity?: string | null;
  worst_severity_label?: string | null;
};

export type ScopedDeviceStatusRollupLike = DeviceStatusRollupLike & {
  scope_type?: string | null;
  scope_id?: number | string | null;
  scope_label?: string | null;
};

export const operationalSeverityRank = (severity?: string | null) => {
  if (severity === "critical") return 3;
  if (severity === "warning") return 2;
  if (severity === "info") return 1;
  if (severity === "ok") return 0;
  return -1;
};

export const operationalSeverityChipColor = (severity?: string | null) => {
  if (severity === "critical") return "error" as const;
  if (severity === "warning") return "warning" as const;
  if (severity === "ok") return "success" as const;
  return "info" as const;
};

export const operationalSeverityLabel = (severity?: string | null) => {
  if (severity === "critical") return "Action needed";
  if (severity === "warning") return "Needs attention";
  if (severity === "ok") return "OK";
  return "Notice";
};

export type OperationalPresentation = {
  status: string;
  label: string;
  summary: string;
  color: string;
  fillColor: string;
  severity?: string | null;
  variant: "filled" | "hollow";
};

export const operationalStatusPalette = {
  current: "#43a047",
  stale: "#f2b84b",
  unavailable: "#e87722",
  issue: "#d32f2f",
  offline: "#8a948f",
  unknown: "#9aa3a0",
};

export const operationalStatusFillPalette = {
  current: "rgba(67, 160, 71, 0.14)",
  stale: "rgba(242, 184, 75, 0.14)",
  unavailable: "rgba(232, 119, 34, 0.14)",
  issue: "rgba(211, 47, 47, 0.14)",
  offline: "rgba(138, 148, 143, 0.08)",
  unknown: "rgba(154, 163, 160, 0.10)",
};

const normalizeOperationalStatus = (status?: string | null, severity?: string | null) => {
  const normalizedStatus = String(status || "").toLowerCase();
  if (["current", "ok", "success", "running", "online", "active"].includes(normalizedStatus)) return "current";
  if (["issue", "failed", "failure", "error"].includes(normalizedStatus)) return "issue";
  if (["stale"].includes(normalizedStatus)) return "stale";
  if (["unavailable"].includes(normalizedStatus)) return "unavailable";
  if (["offline", "disabled"].includes(normalizedStatus)) return "offline";
  if (severity === "critical") return "issue";
  if (severity === "warning") return "stale";
  return "unknown";
};

export const getOperationalStatusPresentation = (
  status?: OperationalStatusLike | null,
  fallbackLabel = "Unknown",
  fallbackSummary = "No operational status is available yet.",
): OperationalPresentation => {
  const normalized = normalizeOperationalStatus(status?.status, status?.severity);
  const label = status?.status
    ? String(status.status).replaceAll("_", " ")
    : fallbackLabel;
  const titleCaseLabel = label.charAt(0).toUpperCase() + label.slice(1);

  return {
    status: normalized,
    label: titleCaseLabel,
    summary: fallbackSummary,
    color: operationalStatusPalette[normalized],
    fillColor: operationalStatusFillPalette[normalized],
    severity: status?.severity,
    variant: normalized === "offline" ? "hollow" : "filled",
  };
};

export const operationalStatusNeedsAttention = (status?: OperationalStatusLike | null) => {
  if (!status) return false;
  return operationalSeverityRank(status.severity) > 0 || !["current", "ok"].includes(String(status.status || ""));
};

const countStatus = (counts: DeviceStatusRollupLike["status_counts"], key: string) =>
  Number(counts?.[key] || 0);

const STATUS_ROLLUP_KEYS = ["current", "issue", "offline", "stale", "unavailable", "unknown"];

const pluralize = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

const needsAttentionPhrase = (count: number, singular: string, plural = `${singular}s`) =>
  `${pluralize(count, singular, plural)} ${count === 1 ? "needs" : "need"} attention`;

const addCountRecord = (
  target: Record<string, number>,
  source: Record<string, number | string | null | undefined> | null | undefined,
) => {
  Object.entries(source || {}).forEach(([key, value]) => {
    target[key] = (target[key] || 0) + Number(value || 0);
  });
};

export const getDeviceStatusRollupCounts = (rollup?: DeviceStatusRollupLike | null) => {
  const counts = rollup?.status_counts || {};
  const current = countStatus(counts, "current");
  const issue = countStatus(counts, "issue");
  const offline = countStatus(counts, "offline");
  const stale = countStatus(counts, "stale");
  const unavailable = countStatus(counts, "unavailable");
  const unknown = countStatus(counts, "unknown");
  const monitorCounts = rollup?.monitor_status_counts || {};
  const monitorCurrent = countStatus(monitorCounts, "current");
  const monitorIssue = countStatus(monitorCounts, "issue");
  const monitorOffline = countStatus(monitorCounts, "offline");
  const monitorStale = countStatus(monitorCounts, "stale");
  const monitorUnavailable = countStatus(monitorCounts, "unavailable");
  const monitorUnknown = countStatus(monitorCounts, "unknown");

  return {
    current,
    attention: issue + offline + stale + unavailable + unknown,
    total: Number(rollup?.device_count || 0),
    monitorCurrent,
    monitorAttention: monitorIssue + monitorOffline + monitorStale + monitorUnavailable + monitorUnknown,
    monitorTotal: Number(rollup?.monitor_count || 0),
    openMessages: Number(rollup?.open_message_count || 0),
    criticalMessages: Number(rollup?.critical_open_message_count || 0),
  };
};

export const summarizeDeviceStatusRollup = (
  rollup?: DeviceStatusRollupLike | null,
  label = "Device status",
) => {
  if (!rollup) return `${label}: n/a`;

  const counts = getDeviceStatusRollupCounts(rollup);
  const parts = [`${counts.current}/${counts.total} ${counts.total === 1 ? "Device" : "Devices"} current`];
  if (counts.monitorTotal > 0) {
    parts.push(`${counts.monitorCurrent}/${counts.monitorTotal} ${counts.monitorTotal === 1 ? "Monitor" : "Monitors"} current`);
  }
  if (counts.attention > 0) {
    parts.push(needsAttentionPhrase(counts.attention, "Device"));
  }
  if (counts.monitorAttention > 0) {
    parts.push(needsAttentionPhrase(counts.monitorAttention, "Monitor"));
  }

  return `${label}: ${parts.join(", ")}`;
};

export const getDeviceStatusRollupPresentation = (
  rollup?: DeviceStatusRollupLike | null,
  label = "Device status",
): OperationalPresentation => {
  if (!rollup) {
    return getOperationalStatusPresentation(null, "Unknown", `${label}: no visible status`);
  }

  const counts = getDeviceStatusRollupCounts(rollup);
  const statusLike = {
    status: rollup.worst_status || (counts.total > 0 ? "current" : "unknown"),
    severity: rollup.worst_severity || (counts.total > 0 ? "ok" : "info"),
  };
  const parts = [`${counts.current}/${counts.total} ${counts.total === 1 ? "Device" : "Devices"} current`];
  if (counts.monitorTotal > 0) {
    parts.push(`${counts.monitorCurrent}/${counts.monitorTotal} ${counts.monitorTotal === 1 ? "Monitor" : "Monitors"} current`);
  }
  if (counts.attention > 0) {
    parts.push(needsAttentionPhrase(counts.attention, "Device"));
  }
  if (counts.monitorAttention > 0) {
    parts.push(needsAttentionPhrase(counts.monitorAttention, "Monitor"));
  }
  if (counts.openMessages > 0) {
    parts.push(`${counts.openMessages} open message${counts.openMessages === 1 ? "" : "s"}`);
  }
  const presentation = getOperationalStatusPresentation(
    statusLike,
    rollup.worst_status_label || "Unknown",
    `${label}: ${parts.join(", ")}`,
  );

  return {
    ...presentation,
    label: rollup.worst_status_label || presentation.label,
  };
};

export const combineDeviceStatusRollups = (
  rollups: Array<ScopedDeviceStatusRollupLike | null | undefined>,
  scope?: Pick<ScopedDeviceStatusRollupLike, "scope_type" | "scope_id" | "scope_label">,
): ScopedDeviceStatusRollupLike => {
  const deviceIds = new Set<number | string>();
  const monitorIds = new Set<number | string>();
  const statusCounts: Record<string, number> = {};
  const monitorStatusCounts: Record<string, number> = {};
  const monitorSeverityCounts: Record<string, number> = {};
  const monitorDetailsById = new Map<number | string, NonNullable<DeviceStatusRollupLike["monitor_details"]>[number]>();
  let deviceCount = 0;
  let monitorCount = 0;
  let openMessageCount = 0;
  let criticalOpenMessageCount = 0;
  let worstRollup: ScopedDeviceStatusRollupLike | null = null;
  let worstRank = -1;

  rollups.filter(Boolean).forEach((rollup) => {
    const nextRollup = rollup as ScopedDeviceStatusRollupLike;
    deviceCount += Number(nextRollup.device_count || 0);
    monitorCount += Number(nextRollup.monitor_count || 0);
    openMessageCount += Number(nextRollup.open_message_count || 0);
    criticalOpenMessageCount += Number(nextRollup.critical_open_message_count || 0);
    (nextRollup.device_ids || []).forEach((deviceId) => deviceIds.add(deviceId));
    (nextRollup.monitor_ids || []).forEach((monitorId) => monitorIds.add(monitorId));
    (nextRollup.monitor_details || []).forEach((detail) => {
      monitorDetailsById.set(detail.monitor_id, detail);
    });

    STATUS_ROLLUP_KEYS.forEach((key) => {
      statusCounts[key] = (statusCounts[key] || 0) + Number(nextRollup.status_counts?.[key] || 0);
      monitorStatusCounts[key] = (monitorStatusCounts[key] || 0) + Number(nextRollup.monitor_status_counts?.[key] || 0);
    });
    addCountRecord(monitorSeverityCounts, nextRollup.monitor_severity_counts);

    const rank = operationalSeverityRank(nextRollup.worst_severity);
    if (rank > worstRank) {
      worstRank = rank;
      worstRollup = nextRollup;
    }
  });

  return {
    scope_type: scope?.scope_type,
    scope_id: scope?.scope_id,
    scope_label: scope?.scope_label,
    device_count: deviceCount,
    device_ids: Array.from(deviceIds),
    monitor_count: monitorCount,
    monitor_ids: Array.from(monitorIds),
    monitor_details: Array.from(monitorDetailsById.values()),
    status_counts: statusCounts,
    monitor_status_counts: monitorStatusCounts,
    monitor_severity_counts: monitorSeverityCounts,
    open_message_count: openMessageCount,
    critical_open_message_count: criticalOpenMessageCount,
    worst_status: worstRollup?.worst_status || (deviceCount > 0 ? "current" : "unknown"),
    worst_status_label: worstRollup?.worst_status_label || (deviceCount > 0 ? "Current" : "Unknown"),
    worst_severity: worstRollup?.worst_severity || (deviceCount > 0 ? "ok" : "info"),
    worst_severity_label: worstRollup?.worst_severity_label || (deviceCount > 0 ? "OK" : "Info"),
  };
};
